import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader.jsx';
import PageState from '../../components/PageState.jsx';
import SectionCard from '../../components/SectionCard.jsx';
import Notice from '../../components/Notice.jsx';
import LifecycleNotice from '../../components/LifecycleNotice.jsx';
import RunNow from '../../components/RunNow.jsx';
import { Dot } from '../../components/StatusWord.jsx';
import KpiCard, { KpiGrid } from '../../components/KpiCard.jsx';
import { api } from '../../lib/api.js';
import { lifecycleNotice } from '../../lib/lifecycle-notice.js';
import { customerSectionPath } from '../../lib/navigation.js';
import { STAFF_ACT_MESSAGE } from '../../lib/staff-act.js';
import { formatMoney, parsePriceText, savingsCaption } from '../../lib/license-format.js';
import { LICENSES_DISCLAIMER, LICENSE_SEAT_HELP, licenseKpiWords, validatePriceInput } from '../../../../shared/license-insights.mjs';
import { collectionPhrase } from '../security/SecurityPage.jsx';
import { SOURCES_ID } from '../data-collection/SourcesCard.jsx';
import FindingList, { UsageLists } from './FindingList.jsx';
import LicensesReportButton from './LicensesReportButton.jsx';
import ProductsTable, { PRODUCTS_ID } from './ProductsTable.jsx';

const TITLE = 'Licenses';
const SUBLINE = 'Microsoft 365 and Entra ID licenses: what is wasted, what to do first, and where.';

/**
 * Licenses (License Insights, spec docs/superpowers/specs/2026-10-25-license-insights-design.md, section 5). Top to
 * bottom: the header (inactivity and grace in the subline, Edit prices and Export CSV), the price notice, three KPIs,
 * Reclaim first and Needs attention, Products, and the disclaimer. Every state of 5.2 has its own rendering.
 *
 * @param {object} props
 * @param {boolean} [props.readOnly] the viewer role: aggregates only, no controls, one line saying so
 * @param {boolean} [props.staffReadOnly] IPC Global staff on a customer below the top tier: sees, never acts
 * @param {(url: string) => void} [props.onOpenReport] receives the signed URL of the Licenses usage PDF (default: open it)
 */
export default function LicensesPage({ customerId, customer, audience, onRefresh, readOnly = false, staffReadOnly = false, onOpenReport }) {
  const [load, setLoad] = useState({ status: 'loading', data: null, error: null });
  const [reloads, setReloads] = useState(0);
  const reload = useCallback(() => setReloads((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    api.getLicenses(customerId)
      .then((data) => { if (!cancelled) setLoad({ status: 'ready', data, error: null }); })
      .catch((e) => { if (!cancelled) setLoad((prev) => ({ status: prev.data ? 'ready' : 'error', data: prev.data, error: e })); });
    return () => { cancelled = true; };
  }, [customerId, reloads]);

  const data = load.data;
  // Lifecycle only while the customer has never been collected (not deployed, awaiting consent, first collection);
  // after that the Licenses states below (no permission, source off, first snapshot pending) say what is missing.
  const hasData = Boolean(data?.view) || Boolean(customer?.lastRunAt);
  const notice = customer ? lifecycleNotice(customer, { audience, hasData }) : null;

  if (notice && !hasData) {
    return (
      <div>
        <PageHeader title={TITLE} subline={SUBLINE} />
        <LifecycleNotice customer={customer} audience={audience} hasData={hasData} onRefresh={onRefresh} readOnly={readOnly} />
      </div>
    );
  }
  if (load.status === 'loading') {
    return <><PageHeader title={TITLE} subline={SUBLINE} /><PageState state="loading" header={false} cards={3} label="Loading licenses" /></>;
  }
  if (load.status === 'error') {
    return (
      <>
        <PageHeader title={TITLE} subline={SUBLINE} />
        <PageState state="error" title="Licenses could not be loaded" message={`${load.error?.message ?? 'The request failed'}. Check your connection, then retry.`} onRetry={reload} />
      </>
    );
  }
  const canAct = data.canEdit === true && !readOnly && !staffReadOnly;
  if (data.state !== 'ready') {
    return <NotReady state={data.state} customer={customer} customerId={customerId} canAct={canAct} onFinished={reload} />;
  }
  return (
    <Ready
      data={data} customerId={customerId} canAct={canAct} readOnly={readOnly} staffReadOnly={staffReadOnly}
      onSaved={reload} onOpenReport={onOpenReport}
    />
  );
}

/** No permission, source off, first snapshot pending (spec 5.2): one full-story notice and what the page will show. */
function NotReady({ state, customer, customerId, canAct, onFinished }) {
  const sourcesTo = `${customerSectionPath(customerId, 'data-collection')}#${SOURCES_ID}`;
  const willShow = (
    <SectionCard title="What this page will show" testId="licenses-will-show">
      <ul className="list-disc space-y-1 pl-5 text-sm text-ipc-ink">
        <li>How much your unused Microsoft licenses cost per month and per year.</li>
        <li>What to reclaim first: licenses on disabled, inactive or duplicate accounts, and seats nobody uses.</li>
        <li>Every product you bought, with seats purchased, assigned, in use and wasted.</li>
      </ul>
    </SectionCard>
  );
  const askAdmin = <span className="text-[13px] text-ipc-muted">Your onGuard admin can do this.</span>;
  if (state === 'no_permission') {
    return (
      <div className="space-y-4">
        <PageHeader title={TITLE} subline={SUBLINE} />
        <Notice
          tone="warn" title="Licenses needs permission to read your subscriptions" testId="licenses-no-permission"
          action={canAct ? <Link className="btn-primary min-h-[40px]" to={sourcesTo}>Add the permission</Link> : askAdmin}
        >
          onGuard reads which Microsoft products you bought and how many seats are used, with the Organization.Read.All
          permission, read only. Without it this page cannot count seats or savings. Turn on Products and seats in Data
          collection; a Global Administrator then approves the permission once in Microsoft.
        </Notice>
        {willShow}
      </div>
    );
  }
  if (state === 'source_off') {
    return (
      <div className="space-y-4">
        <PageHeader title={TITLE} subline={SUBLINE} />
        <Notice
          tone="warn" title="License collection is off" testId="licenses-source-off"
          action={canAct ? <Link className="btn-primary min-h-[40px]" to={sourcesTo}>Turn it on</Link> : askAdmin}
        >
          The Products and seats source is turned off in Data collection, so onGuard has nothing to count. It needs no
          new permission. Turn it on and the next collection fills this page.
        </Notice>
        {willShow}
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <PageHeader title={TITLE} subline={SUBLINE} />
      <SectionCard title="Your first license snapshot is on its way" testId="licenses-pending">
        <p className="text-sm text-ipc-ink">It arrives with the next collection. Nothing to do: onGuard reads your products and seats with the permissions you already granted.</p>
        {canAct && customer ? <div className="mt-4"><RunNow customer={customer} bare heading="Collect now" summary="Start a collection now instead of waiting for the schedule." onFinished={onFinished} /></div> : null}
      </SectionCard>
      {willShow}
    </div>
  );
}

function Ready({ data, customerId, canAct, readOnly, staffReadOnly, onSaved, onOpenReport }) {
  const view = data.view;
  const currency = view.currency;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [resets, setResets] = useState(() => new Set());
  const [errors, setErrors] = useState({});
  const [saveState, setSaveState] = useState({ busy: false, error: null });
  const [exportState, setExportState] = useState({ busy: false, error: null });
  const firstFocus = useRef(null);

  const paidRows = useMemo(() => view.products.filter((p) => !p.free && p.appliesTo === 'User'), [view.products]);
  const startEdit = (focusMissing = false) => {
    const next = {};
    for (const p of paidRows) next[p.skuId] = typeof p.price.amount === 'number' ? p.price.amount.toFixed(2) : '';
    setDraft(next);
    setResets(new Set());
    setErrors({});
    setSaveState({ busy: false, error: null });
    setEditing(true);
    firstFocus.current = focusMissing ? 'missing' : 'first';
  };
  useEffect(() => {
    if (!editing || !firstFocus.current) return;
    const mode = firstFocus.current;
    firstFocus.current = null;
    const missing = paidRows.find((p) => typeof p.price.amount !== 'number');
    const target = mode === 'missing' && missing ? missing.skuId : paidRows[0]?.skuId;
    const el = target ? document.querySelector(`[data-price-input="${target}"]`) : null;
    document.getElementById(PRODUCTS_ID)?.scrollIntoView?.({ block: 'start' });
    el?.focus?.();
  }, [editing, paidRows]);

  const save = async () => {
    const prices = {};
    const errs = {};
    for (const p of paidRows) {
      if (resets.has(p.skuId)) { prices[p.skuId] = null; continue; }
      const text = draft[p.skuId] ?? '';
      const before = typeof p.price.amount === 'number' ? p.price.amount.toFixed(2) : '';
      if (text.trim() === before) continue;
      if (text.trim() === '') { if (p.price.source === 'entered') prices[p.skuId] = null; continue; }
      const v = validatePriceInput(parsePriceText(text));
      if (!v.ok) errs[p.skuId] = v.error; else prices[p.skuId] = v.value;
    }
    setErrors(errs);
    if (Object.keys(errs).length) {
      setSaveState({ busy: false, error: { refusal: false, message: 'Some prices need a fix: see the fields marked in the table.' } });
      return;
    }
    if (Object.keys(prices).length === 0) { setEditing(false); return; }
    setSaveState({ busy: true, error: null });
    try {
      await api.saveLicensePrices(customerId, { prices, version: data.version });
      setEditing(false);
      setSaveState({ busy: false, error: null });
      onSaved();
    } catch (e) {
      setSaveState({ busy: false, error: saveError(e) });
    }
  };

  const doExport = async () => {
    setExportState({ busy: true, error: null });
    try {
      const { filename, csv } = await api.exportLicenses(customerId);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      setExportState({ busy: false, error: null });
    } catch (e) {
      setExportState({ busy: false, error: saveError(e) });
    }
  };

  const actions = canAct ? (
    editing ? (
      <>
        <button type="button" className="btn-primary min-h-[40px]" disabled={saveState.busy} onClick={save} data-testid="licenses-save-prices">{saveState.busy ? 'Saving prices' : 'Save prices'}</button>
        <button type="button" className="btn-secondary min-h-[40px]" disabled={saveState.busy} onClick={() => { setEditing(false); setErrors({}); setSaveState({ busy: false, error: null }); }}>Cancel</button>
      </>
    ) : (
      <button type="button" className="btn-primary min-h-[40px]" onClick={() => startEdit(false)} data-testid="licenses-edit-prices">Edit prices</button>
    )
  ) : null;
  // The Licenses usage PDF: offered while the report function is installed, to the admin and the IPC Global operator
  // (the API decides; the operator request is not a customer-admin act, like the Implementation footprint).
  const reportOffered = data.report?.available === true && data.report?.canGenerate === true && !readOnly && !editing;
  const secondary = (data.canExport && !editing) || reportOffered ? (
    <>
      {reportOffered ? <LicensesReportButton customerId={customerId} {...(onOpenReport ? { onOpen: onOpenReport } : {})} /> : null}
      {data.canExport && !editing ? <button type="button" className="btn-secondary min-h-[40px]" disabled={exportState.busy} onClick={doExport} data-testid="licenses-export">{exportState.busy ? 'Preparing CSV' : 'Export CSV'}</button> : null}
    </>
  ) : null;

  const reclaim = view.reclaim;
  const attention = view.attention;
  const kpiWords = licenseKpiWords(view.kpis);
  const unlockTo = (unlock) => {
    if (readOnly || staffReadOnly) return null;
    if (unlock === 'data-collection' || unlock === 'permissions') return `${customerSectionPath(customerId, 'data-collection')}#${SOURCES_ID}`;
    return null;
  };

  return (
    <div className="space-y-4" data-testid="licenses-page">
      <PageHeader
        title={TITLE}
        subline={<Subline view={view} stale={data.stale} customerId={customerId} canAct={canAct} version={data.version} onSaved={onSaved} readOnly={readOnly} />}
        action={actions}
        secondary={secondary}
      />
      {readOnly ? <p className="text-[13.5px] text-ipc-muted" data-testid="licenses-read-only">Read only: your role shows totals and products; the people and the prices are managed by your onGuard admin.</p> : null}
      {staffReadOnly ? <p className="text-[13.5px] text-ipc-muted" data-testid="licenses-staff-read-only">{STAFF_ACT_MESSAGE}</p> : null}
      {data.namesUnavailable ? <p className="text-[13.5px] text-ipc-muted" data-testid="licenses-names-unavailable">The people behind each finding could not be read from your AWS account just now; the totals are complete. Reload the page in a moment to see them.</p> : null}
      {saveState.error ? <SaveNotice error={saveState.error} onReload={onSaved} title="The prices were not saved" /> : null}
      {exportState.error ? <SaveNotice error={exportState.error} onReload={onSaved} title="The export did not start" /> : null}
      {view.prices.missing > 0 && !editing ? (
        <Notice
          tone="info" title={`Prices missing for ${view.prices.missing} of ${view.prices.paid} paid products`} testId="licenses-prices-missing"
          action={canAct ? <button type="button" className="btn-secondary min-h-[40px]" onClick={() => startEdit(true)}>Edit prices</button> : null}
        >
          Savings count only the {view.prices.priced} products with a price.{' '}
          {canAct ? 'Use Edit prices to add a price per user per month for the others, in the Products table; for an annual commitment, enter the monthly equivalent. ' : 'Your onGuard admin can add the missing prices. '}
          onGuard never looks prices up online: estimates are Microsoft list prices from {formatAsOf(view.prices.asOf)},
          and your agreement price replaces them.
        </Notice>
      ) : null}
      {editing ? (
        <p className="text-[13.5px] text-ipc-muted" data-testid="licenses-editing-hint">
          Type your price per user per month in {currency}; for an annual commitment, enter the monthly equivalent. Prefilled
          values are Microsoft list price estimates ({formatAsOf(view.prices.asOf)}) unless marked as yours.
        </p>
      ) : null}

      <KpiGrid max={3}>
        <KpiCard
          label="Potential savings per month" kpi="savings"
          value={view.kpis.monthly === null ? null : formatMoney(view.kpis.monthly, currency)}
          caption={savingsCaption(view.kpis, view.prices, currency)}
        />
        <KpiCard
          label={kpiWords.licenses.label} kpi="licenses"
          value={kpiWords.licenses.value} unit={kpiWords.licenses.unit}
          variant="bar" percent={kpiWords.licenses.percent}
          caption={kpiWords.licenses.caption} tone={kpiWords.licenses.healthy ? 'good' : 'warn'}
        />
        <KpiCard
          label={kpiWords.seats.label} kpi="seats"
          value={kpiWords.seats.value} unit={kpiWords.seats.unit}
          variant="bar" percent={kpiWords.seats.percent}
          caption={kpiWords.seats.caption} tone={kpiWords.seats.healthy ? 'good' : 'warn'}
        />
      </KpiGrid>
      <p className="text-[13px] text-ipc-muted" data-testid="licenses-help">{LICENSE_SEAT_HELP}</p>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {attention.length ? (
          <div className="lg:order-2">
            <FindingList
              title="Needs attention" description="Access and deadlines, by urgency" findings={attention} currency={currency}
              numbered={false} showSeverity unlockTo={unlockTo} testId="licenses-attention"
            />
          </div>
        ) : null}
        <div className={attention.length ? 'lg:order-1 lg:col-span-2' : 'lg:col-span-3'}>
          <FindingList
            title="Reclaim checklist" findings={reclaim} currency={currency} checklist usage={view.usage}
            description={view.checks.act > 0 ? `Where to act first, ${view.kpis.monthly === null ? 'by seat count' : 'by monthly savings'}` : 'Every check onGuard runs on your licenses'}
            header={view.checks.allOk ? (
              <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-ipc-ink" data-testid="licenses-all-ok"><Dot tone="good" />{view.checks.text}</p>
            ) : <p className="mb-2 text-sm text-ipc-muted" data-testid="licenses-checks-summary">{view.checks.text}</p>}
            unlockTo={unlockTo} testId="licenses-reclaim"
            emptyText="No license data on this collection."
          />
        </div>
      </div>

      {view.usage ? (
        <SectionCard title="Product use" description="Whether people use the products they are licensed for" testId="licenses-usage">
          {view.usage.evaluated ? null : (
            <p className="mb-2 text-sm text-ipc-muted"><span className="font-semibold text-ipc-ink">Not evaluated.</span> {view.usage.reason}</p>
          )}
          <div className="text-sm"><UsageLists usage={view.usage} /></div>
        </SectionCard>
      ) : null}

      <ProductsTable
        products={view.products} currency={currency} editing={editing} draft={draft} errors={errors} asOf={formatAsOf(view.prices.asOf)}
        onDraftChange={(sku, text) => { setDraft((d) => ({ ...d, [sku]: text })); setResets((r) => { const n = new Set(r); n.delete(sku); return n; }); }}
        onReset={(sku) => { setResets((r) => new Set(r).add(sku)); setDraft((d) => ({ ...d, [sku]: '' })); }}
      />

      <p className="text-[13px] text-ipc-muted" data-testid="licenses-disclaimer">{LICENSES_DISCLAIMER}</p>
    </div>
  );
}

function formatAsOf(iso) {
  const t = Date.parse(iso ?? '');
  return Number.isNaN(t) ? iso : new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

/** A refusal says what to do and offers no retry; a race offers a reload; anything else, retry by saving again. */
function saveError(e) {
  const status = e?.status;
  if (status === 403) return { refusal: true, message: `${e?.body?.message ?? e?.message ?? 'Your role cannot change this'}.`.replace(/\.\.$/, '.') };
  if (status === 409) return { refusal: false, conflict: true, message: e?.body?.message ?? 'Someone changed these settings a moment ago. Reload the page to see their change.' };
  if (status === 400) return { refusal: false, message: e?.body?.message ?? e?.message };
  return { refusal: false, message: `${e?.message ?? 'The request failed'}. Your changes are still on the page: save again in a moment.` };
}

function SaveNotice({ error, onReload, title }) {
  return (
    <Notice
      tone={error.refusal ? 'warn' : 'bad'} title={title} testId="licenses-save-error"
      action={error.conflict ? <button type="button" className="btn-secondary min-h-[40px]" onClick={onReload}>Reload</button> : null}
    >
      {error.message}
    </Notice>
  );
}

/**
 * "Microsoft 365 and Entra ID licenses on today's collection at 6:02 AM. Inactive means no sign-in for 90 days." The
 * days are a button that opens the settings in place for whoever may change them (spec 5.1 and Hugo's decision 3).
 */
function Subline({ view, stale, customerId, canAct, version, onSaved, readOnly }) {
  const when = collectionPhrase(view.collectedAt) ?? 'the latest collection';
  const s = view.settings;
  const grace = s.disabledGraceDays > 0 ? ` Disabled accounts count after ${s.disabledGraceDays} days.` : '';
  if (stale) {
    return (
      <span className="inline-flex flex-wrap items-center gap-x-2" data-testid="licenses-stale">
        <Dot tone="warn" />
        <span>From {when.replace(/^the collection of /, 'the collection on ')}. Today's collection has not arrived.</span>
        {!readOnly ? <Link className="font-semibold text-ipc-light hover:underline" to={customerSectionPath(customerId, 'data-collection')}>Open Data collection</Link> : null}
      </span>
    );
  }
  return (
    <span>
      Microsoft 365 and Entra ID licenses on {when}. Inactive means no sign-in for{' '}
      {canAct ? <SettingsPopover customerId={customerId} settings={s} options={view.options} version={version} onSaved={onSaved} /> : <span>{s.inactivityDays} days</span>}.
      {grace}
    </span>
  );
}

function SettingsPopover({ customerId, settings, options, version, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(settings);
  const [state, setState] = useState({ busy: false, error: null });
  const panel = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    panel.current?.querySelector('input:checked')?.focus?.();
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);
  const save = async () => {
    setState({ busy: true, error: null });
    try {
      await api.saveLicenseSettings(customerId, { inactivityDays: form.inactivityDays, disabledGraceDays: form.disabledGraceDays, version });
      setOpen(false);
      setState({ busy: false, error: null });
      onSaved();
    } catch (e) {
      setState({ busy: false, error: saveError(e) });
    }
  };
  return (
    <span className="relative inline-block">
      <button
        type="button" className="tap font-semibold text-ipc-light underline decoration-ipc-field underline-offset-2 hover:decoration-ipc-light"
        aria-expanded={open} aria-controls="license-settings" onClick={() => { setForm(settings); setOpen((o) => !o); }} data-testid="licenses-settings-toggle"
      >
        {settings.inactivityDays} days
      </button>
      {open ? (
        <div id="license-settings" ref={panel} role="group" aria-label="License settings" className="card absolute left-0 z-20 mt-2 w-[19rem] max-w-[calc(100vw-2rem)] space-y-3 text-left text-sm text-ipc-ink" data-testid="licenses-settings">
          <fieldset>
            <legend className="font-semibold">Inactive means no sign-in for</legend>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
              {options.inactivityDays.map((d) => (
                <label key={d} className="inline-flex items-center gap-1.5">
                  <input type="radio" name="inactivityDays" value={d} checked={form.inactivityDays === d} onChange={() => setForm((f) => ({ ...f, inactivityDays: d }))} />
                  {d} days
                </label>
              ))}
            </div>
          </fieldset>
          <fieldset>
            <legend className="font-semibold">Count disabled accounts</legend>
            <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
              {options.disabledGraceDays.map((d) => (
                <label key={d} className="inline-flex items-center gap-1.5">
                  <input type="radio" name="disabledGraceDays" value={d} checked={form.disabledGraceDays === d} onChange={() => setForm((f) => ({ ...f, disabledGraceDays: d }))} />
                  {d === 0 ? 'At once' : `After ${d} days`}
                </label>
              ))}
            </div>
            <p className="mt-1 text-[12.5px] text-ipc-muted">A grace period leaves time to handle the mailbox of someone who left.</p>
          </fieldset>
          {state.error ? <p className="text-[13px] text-ipc-bad" role="alert">{state.error.message}</p> : null}
          <div className="flex gap-2">
            <button type="button" className="btn-primary min-h-[36px]" disabled={state.busy} onClick={save}>{state.busy ? 'Saving' : 'Save'}</button>
            <button type="button" className="btn-secondary min-h-[36px]" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </div>
      ) : null}
    </span>
  );
}

